import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { listLocations, authMe, authLogout } from "@/lib/api";
import { hasPerm } from "@/lib/permissions";

const AppCtx = createContext(null);

export const AppProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocation] = useState("ALL");
  const [loaded, setLoaded] = useState(false);
  // Surfaced to <Layout> for the "Account Inactive" gate. Cleared on next sign-in.
  const [inactiveLogout, setInactiveLogout] = useState(null);
  const pollRef = useRef(null);

  // Bootstrap — if a JWT is stored, validate it. Otherwise leave currentUser null.
  useEffect(() => {
    (async () => {
      let ls = [];
      try {
        ls = await listLocations();
        setLocations(ls);
      } catch (e) {
        console.warn("locations fetch failed", e);
      }
      const token = localStorage.getItem("iva_access_token");
      if (token) {
        try {
          const me = await authMe();
          setCurrentUser(me);
        } catch (e) {
          if (e?.response?.status === 403) {
            setInactiveLogout({ name: "your account" });
          }
          localStorage.removeItem("iva_access_token");
          localStorage.removeItem("iva_current_user_id");
        }
      }
      const savedLoc = localStorage.getItem("iva_current_location") || "ALL";
      // Reconcile against the live locations list. `savedLoc` may be "ALL" or
      // a comma-separated list (e.g. "JEY,RAY"). Drop any codes that no longer
      // exist; collapse to "ALL" when nothing valid remains.
      const reconciled = (() => {
        if (savedLoc === "ALL") return "ALL";
        const valid = (savedLoc || "")
          .split(",")
          .map((c) => c.trim())
          .filter((c) => c && c !== "ALL" && ls.find((l) => l.code === c));
        if (!valid.length) return "ALL";
        if (ls.length > 0 && valid.length === ls.length) return "ALL";
        return valid.join(",");
      })();
      if (reconciled !== savedLoc) {
        localStorage.setItem("iva_current_location", reconciled);
      }
      setCurrentLocation(reconciled);
      setLoaded(true);
    })();
  }, []);

  // Live-poll the signed-in user every 20s via /auth/me. If admin marks them
  // inactive in another tab, the next poll surfaces the gate.
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!currentUser) return;
    pollRef.current = setInterval(async () => {
      try {
        const me = await authMe();
        // Merge updates (role / location_codes / name changes propagate)
        setCurrentUser((u) => ({ ...u, ...me }));
      } catch (e) {
        if (e?.response?.status === 403 || e?.response?.status === 401) {
          setInactiveLogout({ name: currentUser.name });
          setCurrentUser(null);
          localStorage.removeItem("iva_access_token");
          localStorage.removeItem("iva_current_user_id");
        }
      }
    }, 20_000);
    return () => clearInterval(pollRef.current);
  }, [currentUser]);

  const signIn = async (accessToken, user) => {
    localStorage.setItem("iva_access_token", accessToken);
    localStorage.setItem("iva_current_user_id", user.id);
    if (user.location_codes && user.location_codes.length === 1) {
      setCurrentLocation(user.location_codes[0]);
      localStorage.setItem("iva_current_location", user.location_codes[0]);
    }
    setInactiveLogout(null);
    // Enrich the user with module_permissions (login response doesn't include them).
    try {
      const me = await authMe();
      setCurrentUser({ ...user, ...me });
    } catch {
      setCurrentUser(user);
    }
  };

  const signOut = async () => {
    try { await authLogout(); } catch (_) { /* token may be invalid; ignore */ }
    localStorage.removeItem("iva_access_token");
    localStorage.removeItem("iva_current_user_id");
    setCurrentUser(null);
  };

  const switchLocation = (code) => {
    setCurrentLocation(code);
    localStorage.setItem("iva_current_location", code);
  };

  const clearInactiveLogout = () => setInactiveLogout(null);

  // Surface a `users` list compatible with consumers that still iterate over
  // it (e.g. exports `Created By` filter). We expose the signed-in user only;
  // full user CRUD lives in Admin → Users.
  const users = currentUser ? [currentUser] : [];

  return (
    <AppCtx.Provider
      value={{
        users,
        locations,
        currentUser,
        currentLocation,
        switchLocation,
        signIn,
        signOut,
        loaded,
        inactiveLogout,
        clearInactiveLogout,
        // legacy no-ops kept for backwards-compat with any leftover callers
        switchUser: () => {},
        refreshUsers: async () => {},
        refreshLocations: async () => setLocations(await listLocations()),
      }}
    >
      {children}
    </AppCtx.Provider>
  );
};

export const useApp = () => useContext(AppCtx);

/** React hook: `usePerm("av", "add")` → boolean.  Admin always true. */
export const usePerm = (moduleKey, action = "view") => {
  const { currentUser } = useApp() || {};
  return hasPerm(currentUser, moduleKey, action);
};

export const can = (user, action) => {
  if (!user) return false;
  const role = user.role;
  if (role === "admin") return true;
  // Role keys: parts_manager, spare_executive (UI: "Parts Executive"),
  // floor_supervisor, works_manager, billing_team, crm
  const map = {
    // ---- Spares (Orders & Workbench) ----
    create_order: ["parts_manager", "spare_executive"],
    edit_order: ["parts_manager", "spare_executive"],
    workbench_action: ["parts_manager", "spare_executive"], // mark-ordered, EDD, receive, invoice, reminder, CNR, return, cancel
    delete_order: [], // admin-only
    delete_order_advance: ["parts_manager"],
    orders_export: ["parts_manager"], // admin + parts_manager only
    // ---- Spares (Packing Slips) ----
    packing_slip_write: ["parts_manager", "spare_executive"], // create / edit / dispatch / delete
    // ---- Spares (Stock Planning) ----
    stock_planning_write: ["parts_manager"], // upload / delete / calculate / generate / verify / export
    stock_planning_view: ["parts_manager", "spare_executive", "floor_supervisor", "works_manager", "billing_team", "crm"],
    // ---- Reports ----
    reports_view: ["parts_manager", "spare_executive", "floor_supervisor", "works_manager", "billing_team", "crm"],
    reports_export: ["parts_manager"], // admin + parts_manager only
    // ---- Admin ----
    admin: [], // admin-only
    view_deleted: [], // admin-only
    view_activity: ["works_manager"], // admin + works_manager
    manage_user_status: [], // admin-only
    parts_master_upload: ["parts_manager"], // admin + parts_manager
    // ---- AV ----
    av_view: ["parts_manager", "spare_executive", "floor_supervisor", "works_manager", "billing_team", "crm"],
    av_create: ["floor_supervisor"], // admin + floor_supervisor
    av_edit_core: ["floor_supervisor"], // admin + floor_supervisor
    av_estimate: ["floor_supervisor"],
    av_documents: ["floor_supervisor"],
    av_parts: ["parts_manager"],
    av_repair: ["works_manager", "floor_supervisor"], // admin + WM + FS
    av_billing: ["billing_team", "floor_supervisor"],
    av_advances: ["billing_team", "floor_supervisor"],
    av_hold_deliver: ["floor_supervisor", "works_manager"],
    av_crm_note_add: ["crm"],
    av_crm_note_delete: [], // admin-only
    // ---- Pending Vehicles (Stage 1) ----
    pv_view: ["service_advisor", "workshop_manager", "service_manager", "crm"],
    pv_upload: ["service_advisor", "workshop_manager", "service_manager"], // no CRM
    pv_write: ["service_advisor", "workshop_manager", "service_manager"],
    // ---- PV reopen (Stage 5) — SA excluded ----
    pv_reopen: ["workshop_manager", "service_manager"],
    // ---- PV Status Master ----
    pv_status_view: ["workshop_manager", "service_manager"],
    pv_status_write: [], // admin-only
    // ---- Security Module ----
    security_view: ["security"],
    security_write: ["security"],
    // ---- LOC Tracker (Vehicle Retention) ----
    loc_view: ["service_advisor", "workshop_manager", "service_manager", "floor_supervisor", "works_manager", "crm"],
    loc_write: ["service_advisor", "workshop_manager", "service_manager", "floor_supervisor", "works_manager"],
  };
  return (map[action] || []).includes(role);
};
